import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Avatar,
  Divider,
  Paper
} from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const resumeMeta = {
  hasPhoto: true,
  columns: 1,
  colorOptions: [
    { value: "#1a2b47", label: "Dark Navy" },
    { value: "#4527a0", label: "Deep Purple" },
    { value: "#00695c", label: "Deep Teal" },
    { value: "#d84315", label: "Fiery Orange" },
    { value: "#5d4037", label: "Warm Brown" },
    { value: "#0277bd", label: "Strong Blue" }
  ]
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "John",
  lastName: "Doe",
  currentPosition: "Senior Software Engineer",
  phone: "+91 98765 43210",
  email: "john.doe@example.com",
  city: "Chennai, Tamil Nadu",
  website: "www.johndoeportfolio.com",
  profile: "Experienced software engineer with 8+ years of expertise in full-stack development, cloud architecture, and team leadership. Proven track record of delivering scalable solutions for enterprise clients.",
  skills: [
    "JavaScript, TypeScript, Python",
    "React, Angular, Vue.js",
    "Node.js, Express, Django",
    "AWS, Docker, Kubernetes",
    "MongoDB, PostgreSQL, Redis"
  ],
};



const defaultExperience = [
  {
    jobTitle: "Senior Software Engineer",
    employer: "Tech Solutions Inc.",
    startMonth: "June",
    startYear: "2019",
    endMonth: "Present",
    endYear: "",
    current: true,
    description: "Lead a team of 10 developers in building cloud-native applications. Improved system performance by 40% through code optimization. Implemented CI/CD pipelines reducing deployment time by 60%. Mentored junior developers and conducted code reviews."
  },
  {
    jobTitle: "Software Developer",
    employer: "Digital Innovations Pvt Ltd",
    startMonth: "July",
    startYear: "2015",
    endMonth: "May",
    endYear: "2019",
    current: false,
    description: "Developed and maintained web applications using React and Node.js. Collaborated with cross-functional teams to deliver features on schedule. Reduced API response time by 30% through database optimization. Implemented automated testing increasing coverage to 85%."
  }
];

const defaultProjects = [
  "E-commerce Platform: Built a full-stack e-commerce solution serving 10,000+ daily users with React, Node.js, and MongoDB",
  "AI Chatbot: Developed an intelligent customer support chatbot using Python and NLP, reducing support tickets by 70%",
  "Real-time Analytics Dashboard: Created a dashboard for monitoring application metrics with real-time updates using WebSockets"
];

const defaultCertifications = [
  { name: "AWS Solutions Architect", provider: "Amazon Web Services", year: "2021" },
  { name: "Google Cloud Professional", provider: "Google Cloud", year: "2020" },
  { name: "Scrum Master Certified", provider: "Scrum Alliance", year: "2019" }
];

const defaultLanguages = [
  { name: "English", level: "Fluent" },
];

const defaultSoftwareSkills = [
  { name: "React", level: "95" },
  { name: "Node.js", level: "90" },
];

const defaultAdditionalInfo = [
  "Published 3 research papers in international journals on machine learning",
  "Active contributor to open-source projects on GitHub (500+ commits)",
  "Speaker at TechCon 2022 on 'Modern Web Development Practices'"
];

const Resume27 = ({
  color = "#1a2b47",
  font = "Arial",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "24px",
  sectionSpacing = 20,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 40,
  sideMargins = 40,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const data = { ...defaultData, ...formData };

  const [savedSkills, setSavedSkills] = useState(defaultData.skills.map(skill => ({ name: skill, rating: "" })));
  const [savedSummary, setSavedSummary] = useState(defaultData.profile);
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState(defaultAdditionalInfo);
  const [savedLanguages, setSavedLanguages] = useState(defaultLanguages);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState(defaultCertifications);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState(defaultSoftwareSkills);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  useEffect(() => {
    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) {
      setSavedSkills(JSON.parse(storedSkills));
    }
    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) {
      setSavedSummary(storedSummaries[storedSummaries.length - 1]);
    }
    const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    if (storedAdditionalInfo.length > 0) {
      setSavedAdditionalInfo(storedAdditionalInfo);
    }
    const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
    if (storedLanguages.length > 0) {
      setSavedLanguages(storedLanguages);
    }
    const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(storedAccomplishments);
    const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
    if (storedCertifications.length > 0) {
      setSavedCertifications(storedCertifications);
    }
    const storedReferences = JSON.parse(localStorage.getItem("referencesList")) || [];
    setSavedReferences(storedReferences);
    const storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills")) || [];
    if (storedSoftwareSkills.length > 0) {
      setSoftwareSkills(storedSoftwareSkills);
    }
    const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(storedVolunteering);
    const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(storedInterests);
    const storedWebsites = JSON.parse(localStorage.getItem("websites")) || [];
    setSavedWebsites(storedWebsites);
    
    const userId = localStorage.getItem("userId");
    
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      
      if (currentCustomSections.length > 0) {
        currentCustomSections.forEach(sectionName => {
          const userCustomKey = `custom_${userId}_${sectionName}`;
          try {
            const customSectionData = localStorage.getItem(userCustomKey);
            if (customSectionData) {
              const parsedData = JSON.parse(customSectionData);
              const items = parsedData.items || [];
              
              const validItems = items.filter(item => item && item.trim() !== '');
              
              if (validItems.length > 0) {
                sectionsData[sectionName] = validItems;
              }
            }
          } catch (error) {
            console.error(`Error loading current custom section ${sectionName}:`, error);
          }
        });
      }
    }

    setCustomSections(sectionsData);
  }, []);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  const textStyle = {
    color: color,
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`
  };

  return (
    <Box sx={{ 
      width: "210mm",
      height: "297mm",
      boxShadow: 3, 
      borderRadius: '8px', 
      overflow: 'hidden',
      display: 'flex',
      bgcolor: '#fff',
      fontFamily: font,
      color: color
    }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          bgcolor: "#fff",
          color: color,
          fontFamily: font,
          fontSize,
          fontStyle,
          lineHeight: `${lineSpacing}px`,
          p: `${topBottomMargin}px ${sideMargins}px`,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          "& *": {
            fontFamily: `${font} !important`,
            color: `${color} !important`,
            lineHeight: `${lineSpacing}px !important`,
          },
        }}
      >
        {/* Header Section */}
        <Grid container spacing={2} alignItems="center" sx={{ 
          fontFamily: font, 
          "& *": { 
            fontFamily: `${font} !important`, 
            color: `${color} !important`,
            lineHeight: `${lineSpacing}px !important`,
          } 
        }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ 
                  fontSize: "28px", 
                  fontWeight: "bold", 
                  color, 
                  fontFamily: font,
                  lineHeight: "32px"
                }}>
                  {data.firstName} {data.lastName}
                </Typography>
                <Typography variant="subtitle1" sx={{ 
                  fontFamily: font, 
                  color: color,
                  fontSize: "18px",
                  lineHeight: "24px",
                  mt: 0.5
                }}>
                  {data.currentPosition}
                </Typography>
              </Box>
              <Avatar
                src={photo}
                sx={{ 
                  width: 100, 
                  height: 100, 
                  border: `${lineWeight}px solid ${color}`,
                  ml: 2,
                  flexShrink: 0
                }}
              />
            </Box>
            
            <Grid container spacing={1} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ 
                  fontFamily: font, 
                  color: color,
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: `${lineSpacing}px`
                }}>
                  📧 {data.email}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ 
                  fontFamily: font, 
                  color: color,
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: `${lineSpacing}px`
                }}>
                  📞 {data.phone}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ 
                  fontFamily: font, 
                  color: color,
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: `${lineSpacing}px`
                }}>
                  📍 {data.city}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ 
          my: 3, 
          borderColor: color, 
          borderWidth: `${lineWeight}px`,
          fontFamily: font 
        }} />

        {/* Two Column Layout */}
        <Grid container spacing={4} sx={{ flex: 1 }}>
          {/* LEFT COLUMN */}
          <Grid item xs={12} md={6}>
            {/* Profile Section */}
            <Box 
              mb={`${sectionSpacing}px`} 
              className="resume-section" 
              id="profile-section" 
              sx={{ 
                fontFamily: font,
                ...textStyle
              }}
            >
              <Typography sx={{ 
                fontSize: headingSize, 
                fontWeight: "bold", 
                color, 
                fontFamily: font,
                mb: `${paragraphSpacing}px`
              }}>
                Profile
              </Typography>
              <Typography sx={{ 
                mt: `${paragraphSpacing / 4}px`, 
                textIndent: `${paragraphIndent}px`, 
                fontFamily: font,
                color: color,
                lineHeight: `${lineSpacing}px`
              }}>
                {savedSummary || data.profile}
              </Typography>
            </Box>

            {/* Skills Section */}
            <Box 
              mb={`${sectionSpacing}px`} 
              className="resume-section" 
              id="skills-section" 
              sx={{ 
                fontFamily: font,
                ...textStyle
              }}
            >
              <Typography sx={{ 
                fontSize: headingSize, 
                fontWeight: "bold", 
                color, 
                fontFamily: font,
                mb: `${paragraphSpacing}px`
              }}>
                Skills
              </Typography>
              <ul style={{ 
                paddingLeft: "20px", 
                marginTop: 4, 
                fontFamily: font, 
                listStyleType: "none",
                color: color,
                lineHeight: `${lineSpacing}px`
              }}>
                {savedSkills.length > 0 ? (
                  savedSkills.map((skill, i) => (
                    <li key={i} style={{ 
                      fontFamily: font, 
                      marginBottom: `${paragraphSpacing / 2}px`, 
                      color: color,
                      lineHeight: `${lineSpacing}px`
                    }}>
                      {skill.name} {skill.rating ? `(${skill.rating}%)` : ''}
                    </li>
                  ))
                ) : (
                  data.skills.map((skill, i) => (
                    <li key={i} style={{ 
                      fontFamily: font, 
                      marginBottom: `${paragraphSpacing / 2}px`, 
                      color: color,
                      lineHeight: `${lineSpacing}px`
                    }}>
                      {skill}
                    </li>
                  ))
                )}
              </ul>
            </Box>

       

            {/* Experience Section */}
            <Box 
              mb={`${sectionSpacing}px`} 
              className="resume-section" 
              id="experience-section" 
              sx={{ 
                fontFamily: font,
                ...textStyle
              }}
            >
              <Typography sx={{ 
                fontSize: headingSize, 
                fontWeight: "bold", 
                color, 
                fontFamily: font,
                mb: `${paragraphSpacing}px`
              }}>
                Experience
              </Typography>
              {workExperiences && workExperiences.length > 0 ? (
                workExperiences.map((work, i) => (
                  <Box key={i} mb={`${paragraphSpacing}px`} sx={{ fontFamily: font }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                      fontFamily: font, 
                      color: color,
                      lineHeight: `${lineSpacing}px`
                    }}>
                      {work.jobTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontFamily: font, 
                      color: color,
                      lineHeight: `${lineSpacing}px`
                    }}>
                      {work.employer} | {formatDate(work.startMonth, work.startYear)} - {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                    </Typography>
                    {work.description && (
                      <Box
                        sx={{ 
                          mt: 1, 
                          fontFamily: font, 
                          color: color,
                          lineHeight: `${lineSpacing}px`
                        }}
                        dangerouslySetInnerHTML={{
                          __html: work.description
                            .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family:${font}; color:${color}; line-height:${lineSpacing}px">$1</strong>`)
                            .replace(/\*(.*?)\*/g, `<em style="font-family:${font}; color:${color}; line-height:${lineSpacing}px">$1</em>`)
                            .replace(/<u>(.*?)<\/u>/g, `<u style="font-family:${font}; color:${color}; line-height:${lineSpacing}px">$1</u>`)
                            .replace(/\n/g, `<br style="font-family:${font}; color:${color}; line-height:${lineSpacing}px"/>`),
                        }}
                      />
                    )}
                  </Box>
                ))
              ) : (
                defaultExperience.map((work, i) => (
                  <Box key={i} mb={`${paragraphSpacing}px`} sx={{ fontFamily: font }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                      fontFamily: font, 
                      color: color,
                      lineHeight: `${lineSpacing}px`
                    }}>
                      {work.jobTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontFamily: font, 
                      color: color,
                      lineHeight: `${lineSpacing}px`
                    }}>
                      {work.employer} | {formatDate(work.startMonth, work.startYear)} - {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                    </Typography>
                    {work.description && (
                      <Box
                        sx={{ 
                          mt: 1, 
                          fontFamily: font, 
                          color: color,
                          lineHeight: `${lineSpacing}px`
                        }}
                        dangerouslySetInnerHTML={{
                          __html: work.description
                            .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family:${font}; color:${color}; line-height:${lineSpacing}px">$1</strong>`)
                            .replace(/\*(.*?)\*/g, `<em style="font-family:${font}; color:${color}; line-height:${lineSpacing}px">$1</em>`)
                            .replace(/<u>(.*?)<\/u>/g, `<u style="font-family:${font}; color:${color}; line-height:${lineSpacing}px">$1</u>`)
                            .replace(/\n/g, `<br style="font-family:${font}; color:${color}; line-height:${lineSpacing}px"/>`),
                        }}
                      />
                    )}
                  </Box>
                ))
              )}
            </Box>
                {savedLanguages.length > 0 && (
              <Box 
                mb={`${sectionSpacing}px`} 
                className="resume-section" 
                id="languages-section" 
                sx={{ 
                  fontFamily: font,
                  ...textStyle
                }}
              >
                <Typography sx={{ 
                  fontSize: headingSize, 
                  fontWeight: "bold", 
                  color, 
                  fontFamily: font,
                  mb: `${paragraphSpacing}px`
                }}>
                  Languages
                </Typography>
                {savedLanguages.map((lang, i) => (
                  <Typography key={i} variant="body2" sx={{ 
                    fontFamily: font, 
                    color: color,
                    lineHeight: `${lineSpacing}px`
                  }}>
                    {lang.name} - {lang.level || "Fluent"}
                  </Typography>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Resume27;